import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, CalendarDays, Dumbbell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { tr, weekdays } from "@/data/i18n";

export default function Calendar() {
  const { lang, user, loading } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [entries, setEntries] = useState({}); // weekday -> {plan_id, day_index}
  const [busy, setBusy] = useState(false);

  const wd = weekdays(lang);
  const todayIdx = (new Date().getDay() + 6) % 7; // Mon=0

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    Promise.all([api.listPlans(), api.getSchedule()]).then(([p, s]) => {
      setPlans(p.data);
      const map = {};
      (s.data.entries || []).forEach((e) => { map[e.weekday] = { plan_id: e.plan_id, day_index: e.day_index }; });
      setEntries(map);
    }).catch(() => {});
  }, [user, loading, navigate]);

  const setPlanFor = (weekday, plan_id) => {
    setEntries((m) => ({ ...m, [weekday]: { plan_id, day_index: 0 } }));
  };
  const setDayFor = (weekday, day_index) => {
    setEntries((m) => ({ ...m, [weekday]: { ...m[weekday], day_index: Number(day_index) } }));
  };
  const clearDay = (weekday) => {
    setEntries((m) => { const n = { ...m }; delete n[weekday]; return n; });
  };

  const planById = (id) => plans.find((p) => p.id === id);

  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.entries(entries).filter(([, v]) => v?.plan_id).map(([weekday, v]) => {
        const p = planById(v.plan_id);
        const day = p?.days?.[v.day_index];
        return { weekday: Number(weekday), plan_id: v.plan_id, plan_title: p?.title || "", day_index: v.day_index, day_name: day?.name || "" };
      });
      await api.putSchedule(payload);
      toast.success(tr(lang, "schedule_saved"));
    } catch (e) { toast.error("Errore"); }
    setBusy(false);
  };

  const todayEntry = entries[todayIdx];
  const todayPlan = todayEntry && planById(todayEntry.plan_id);
  const todayDay = todayPlan?.days?.[todayEntry.day_index];

  // dates for current week (Mon..Sun)
  const monday = new Date();
  monday.setDate(monday.getDate() - todayIdx);
  const weekDates = wd.map((_, i) => {
    const d = new Date(monday); d.setDate(monday.getDate() + i); return d;
  });

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold uppercase text-white sm:text-4xl">{tr(lang, "calendar_title")}</h1>
          <p className="mt-2 text-zinc-500">{tr(lang, "calendar_sub")}</p>
        </div>
        <button onClick={save} disabled={busy} data-testid="save-schedule-btn"
                className="inline-flex items-center gap-2 rounded-full bg-red-500 px-5 py-2.5 font-semibold text-white hover:bg-red-600 red-glow disabled:opacity-60">
          <Save size={16} /> {tr(lang, "save_plan")}
        </button>
      </div>

      {/* Today banner */}
      <div className="mt-6 rounded-2xl border border-red-500/30 bg-gradient-to-r from-red-500/10 to-orange-500/10 p-5" data-testid="today-banner">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-red-400"><CalendarDays size={15} /> {tr(lang, "today_workout")}</div>
        {todayDay ? (
          <button onClick={() => navigate(`/plan/${todayPlan.id}`)} className="mt-1 text-left">
            <div className="font-display text-xl font-bold text-white">{todayDay.name} <span className="text-zinc-500">· {todayPlan.title}</span></div>
          </button>
        ) : (
          <div className="mt-1 text-zinc-400">{tr(lang, "no_today")} 😴</div>
        )}
      </div>

      {plans.length === 0 && (
        <div className="mt-8 rounded-2xl border border-dashed border-zinc-800 p-8 text-center text-zinc-500">
          <Dumbbell className="mx-auto mb-2 text-zinc-600" />
          {tr(lang, "no_saved")}
          <div><button onClick={() => navigate("/create/ai")} className="mt-3 rounded-full bg-red-500 px-4 py-2 text-sm font-semibold text-white">{tr(lang, "mode_ai_title")}</button></div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-7">
        {wd.map((label, i) => {
          const e = entries[i];
          const p = e && planById(e.plan_id);
          const day = p?.days?.[e?.day_index ?? 0];
          const isToday = i === todayIdx;
          const active = !!p;
          return (
            <div key={i} data-testid={`weekday-${i}`}
                 className={`relative flex min-h-[220px] flex-col overflow-hidden rounded-2xl border p-4 transition-all ${
                   isToday ? "border-red-500 bg-gradient-to-b from-red-500/15 to-zinc-950 red-glow"
                   : active ? "border-red-500/30 bg-zinc-900/70" : "border-zinc-800 bg-zinc-900/40"}`}>
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <div className={`font-display text-2xl font-extrabold uppercase leading-none ${isToday ? "text-red-400" : "text-white"}`}>{label}</div>
                  <div className="mt-0.5 text-xs text-zinc-500">{weekDates[i].getDate()}/{weekDates[i].getMonth() + 1}</div>
                </div>
                {isToday && <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white">{tr(lang, "today_workout").split(" ")[0]}</span>}
              </div>

              {active ? (
                <div className="flex flex-1 flex-col">
                  <div className="mb-2 flex items-center gap-1.5 rounded-lg bg-red-500/15 px-2.5 py-1.5 text-xs font-bold text-red-300">
                    <Dumbbell size={13} /> {day?.name || `${tr(lang, "day")} ${(e.day_index ?? 0) + 1}`}
                  </div>
                  <div className="mb-3 line-clamp-2 text-sm font-semibold text-white">{p.title}</div>
                </div>
              ) : (
                <div className="flex flex-1 items-center justify-center text-sm font-medium text-zinc-600">{tr(lang, "rest_day")} 😴</div>
              )}

              <div className="mt-auto space-y-2">
                <select value={e?.plan_id || ""} onChange={(ev) => ev.target.value ? setPlanFor(i, ev.target.value) : clearDay(i)} data-testid={`weekday-plan-${i}`}
                        className="w-full appearance-none rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-xs text-white outline-none focus:border-red-500">
                  <option value="">— {tr(lang, "rest_day")} —</option>
                  {plans.map((pl) => <option key={pl.id} value={pl.id}>{pl.title}</option>)}
                </select>
                {p && p.days?.length > 1 && (
                  <select value={e?.day_index ?? 0} onChange={(ev) => setDayFor(i, ev.target.value)} data-testid={`weekday-day-${i}`}
                          className="w-full appearance-none rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-xs text-white outline-none focus:border-red-500">
                    {p.days.map((d, di) => <option key={di} value={di}>{d.name || `${tr(lang, "day")} ${di + 1}`}</option>)}
                  </select>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
